import { camelize } from 'humps';
import { isEmpty, omit } from 'ramda';
import {
  ScenarioData,
  Submission,
  SubmissionsSubjectField,
  SubmissionWur2Query,
} from '#types/generated/graphql';
import { roundNumber } from '#utils/valueFormatters';
import {
  REMOVE_EXPLORE_REPORTER_FIELDS_FROM_YEAR,
  REMOVE_OUTBOUND_EXCHANGE_STUDENTS_FROM_YEAR,
} from '../constants';

/**
 * IMPORTANT:
 * The two fields:
 *  - numberOfResearchDoctoratesAwarded
 *  - numberOfProfessionalDoctoratesAwarded
 * must be INCLUDED only when `scenarioMethodologyYear < REMOVE_EXPLORE_REPORTER_FIELDS_FROM_YEAR`.
 * To achieve this, we expose year-aware getters (getMeasuresMapForYear, getFormFieldsWur2ForYear, getFormFieldsWur3ForYear)
 * and use them where we have the scenario year (e.g. getScenarioParametersTableData).
 */

export interface ScenarioDataType extends ScenarioData {
  [key: string]: any;
}

export interface SubmissionDataType
  extends Omit<Submission, 'year' | 'institutionId' | 'id' | 'resource'> {
  [key: string]: any;
}

export type LevelDefinitionsObjectType = {
  [key: string]: {
    parentId?: string;
    levelId?: string;
    shortName?: string;
    longName?: string;
    name?: string;
    weighting?: number;
    type?: string;
    rankingYear?: number;
  }[];
};

export type Subject =
  | 'Overall'
  | 'Arts and Humanities'
  | 'Business and Economics'
  | 'Clinical and Health'
  | 'Computer Science'
  | 'Education'
  | 'Engineering'
  | 'Law'
  | 'Life Sciences'
  | 'Physical Sciences'
  | 'Psychology'
  | 'Social Sciences';

export type SubjectId =
  | 'overall'
  | 'artsAndHumanities'
  | 'businessAndEconomics'
  | 'clinicalAndHealth'
  | 'computerScience'
  | 'education'
  | 'engineering'
  | 'law'
  | 'lifeSciences'
  | 'physicalSciences'
  | 'psychology'
  | 'socialSciences';

export type FieldKeys =
  | 'numberOfAcademicStaff'
  | 'numberOfAcademicStaffOfInternationalOrOverseasOrigin'
  | 'numberOfResearchStaff'
  | 'totalNumberOfStudents'
  | 'totalNumberOfStudentsOfInternationalOrOverseasOrigin'
  | 'totalNumberOfOutboundExchangeStudents'
  | 'percentileFieldWeightedCitationImpact'
  | 'influenceOfPublications'
  | 'numberOfPatentsCitingResearch'
  | 'numberOfPublicationsInTheTop10Percentile'
  | 'numberOfUndergraduateDegreesAwarded'
  | 'numberOfDoctoratesAwarded'
  | 'numberOfResearchDoctoratesAwarded'
  | 'numberOfProfessionalDoctoratesAwarded'
  | 'totalInstitutionalIncome'
  | 'researchIncome'
  | 'researchIncomeFromIndustryAndCommerce'
  | 'researchVotes'
  | 'teachingVotes'
  | 'totalPublications'
  | 'citationScoreCnNormalised'
  | 'citationScoreNormalised'
  | 'internationalPublications';

export const subjects: Subject[] = [
  'Overall',
  'Arts and Humanities',
  'Business and Economics',
  'Clinical and Health',
  'Computer Science',
  'Education',
  'Engineering',
  'Law',
  'Life Sciences',
  'Physical Sciences',
  'Psychology',
  'Social Sciences',
];

export const subjectsMap = {
  overall: 'Overall',
  artsAndHumanities: 'Arts and Humanities',
  businessAndEconomics: 'Business and Economics',
  clinicalAndHealth: 'Clinical and Health',
  computerScience: 'Computer Science',
  education: 'Education',
  engineering: 'Engineering',
  law: 'Law',
  lifeSciences: 'Life Sciences',
  physicalSciences: 'Physical Sciences',
  psychology: 'Psychology',
  socialSciences: 'Social Sciences',
};

const baseMeasuresMap = {
  numberOfAcademicStaff: 'Number of academic staff (FTE)',
  numberOfAcademicStaffOfInternationalOrOverseasOrigin:
    'Number of academic staff of international/overseas origin (FTE)',
  numberOfResearchStaff: 'Number of research staff (FTE)',
  totalNumberOfStudents: 'Total number of students (FTE)',
  totalNumberOfStudentsOfInternationalOrOverseasOrigin:
    'Number of students of international/overseas origin (FTE)',
  totalNumberOfOutboundExchangeStudents: 'Number of outbound exchange students',
  percentileFieldWeightedCitationImpact:
    '75ᵗʰ Percentile Field Weighted Citation Impact',
  influenceOfPublications: 'Influence of publications',
  numberOfPatentsCitingResearch: 'Number of patents citing your research',
  numberOfPublicationsInTheTop10Percentile:
    'Number of publications in the top 10 percentile',
  numberOfUndergraduateDegreesAwarded:
    'Number of Undergraduate Degrees awarded',
  numberOfDoctoratesAwarded: 'Number of Doctorates awarded',
  totalInstitutionalIncome: 'Total institutional income',
  researchIncome: 'Research income',
  researchIncomeFromIndustryAndCommerce:
    'Research income from industry and commerce',
  researchVotes: 'Research Votes',
  teachingVotes: 'Teaching Votes',
  totalPublications: 'Total number of publications',
  citationScoreCnNormalised:
    'Field Weighted Citation Impact (country normalised)',
  citationScoreNormalised: 'Field Weighted Citation Impact (normalised)',
  internationalPublications: 'Internationally co-authored publications',
};

const extraDoctoratesMap: Record<
  'numberOfResearchDoctoratesAwarded' | 'numberOfProfessionalDoctoratesAwarded',
  string
> = {
  numberOfResearchDoctoratesAwarded: 'Number of research Doctorates awarded',
  numberOfProfessionalDoctoratesAwarded:
    'Number of professional Doctorates awarded',
};

export const getMeasuresMapForYear = (year: number) => ({
  ...baseMeasuresMap,
  ...(year < REMOVE_EXPLORE_REPORTER_FIELDS_FROM_YEAR
    ? extraDoctoratesMap
    : {}),
});

export const subjectIds = (subjects.map((name) =>
  camelize(name),
) as unknown) as SubjectId[];

/** Base WUR2 list without the extra doctorates */
const baseFormFieldsWur2: FieldKeys[] = [
  'numberOfAcademicStaff',
  'numberOfAcademicStaffOfInternationalOrOverseasOrigin',
  'numberOfResearchStaff',
  'totalNumberOfStudents',
  'totalNumberOfStudentsOfInternationalOrOverseasOrigin',
  'numberOfUndergraduateDegreesAwarded',
  'numberOfDoctoratesAwarded',
  'totalInstitutionalIncome',
  'researchIncome',
  'researchIncomeFromIndustryAndCommerce',
  'researchVotes',
  'teachingVotes',
  'totalPublications',
  'citationScoreCnNormalised',
  'citationScoreNormalised',
  'internationalPublications',
];

const extraDoctoratesFields: FieldKeys[] = [
  'numberOfResearchDoctoratesAwarded',
  'numberOfProfessionalDoctoratesAwarded',
];

export const getFormFieldsWur2ForYear = (year: number): FieldKeys[] => [
  ...baseFormFieldsWur2,
  ...(year < REMOVE_EXPLORE_REPORTER_FIELDS_FROM_YEAR
    ? extraDoctoratesFields
    : []),
];

export const getFormFieldsWur3ForYear = (year: number): FieldKeys[] => [
  ...getFormFieldsWur2ForYear(year),
  ...(year < REMOVE_OUTBOUND_EXCHANGE_STUDENTS_FROM_YEAR
    ? (['totalNumberOfOutboundExchangeStudents'] as FieldKeys[])
    : []),
  'percentileFieldWeightedCitationImpact',
  'influenceOfPublications',
  'numberOfPatentsCitingResearch',
  'numberOfPublicationsInTheTop10Percentile',
];

export const fieldsMeta: {
  [key in FieldKeys]: {
    overall?: {
      mandatory?: boolean;
      ignored?: boolean;
      maxValue?: boolean;
    };
    subject?: {
      mandatory?: boolean;
      ignored?: boolean;
      maxValue?: boolean;
    };
  };
} = {
  numberOfAcademicStaff: {
    overall: { mandatory: true },
    subject: { mandatory: true, maxValue: true },
  },
  numberOfAcademicStaffOfInternationalOrOverseasOrigin: {
    overall: { mandatory: true, maxValue: true },
    subject: { mandatory: false, maxValue: true },
  },
  numberOfResearchStaff: {
    overall: { mandatory: true, maxValue: true },
    subject: { mandatory: true, maxValue: true },
  },
  totalNumberOfStudents: {
    overall: { mandatory: true, maxValue: true },
    subject: { mandatory: false, maxValue: true },
  },
  totalNumberOfStudentsOfInternationalOrOverseasOrigin: {
    overall: { mandatory: true, maxValue: true },
    subject: { mandatory: false, maxValue: true },
  },
  totalNumberOfOutboundExchangeStudents: {
    overall: { mandatory: true, maxValue: true },
    subject: { mandatory: true, maxValue: true },
  },
  percentileFieldWeightedCitationImpact: {
    overall: { mandatory: true, maxValue: true },
    subject: { mandatory: true, maxValue: true },
  },
  influenceOfPublications: {
    overall: { mandatory: false, ignored: true },
    subject: { mandatory: true },
  },
  numberOfPatentsCitingResearch: {
    overall: { mandatory: false, ignored: true },
    subject: { mandatory: true },
  },
  numberOfPublicationsInTheTop10Percentile: {
    overall: { mandatory: false, ignored: true },
    subject: { mandatory: true },
  },
  numberOfUndergraduateDegreesAwarded: {
    overall: { mandatory: true },
    subject: { mandatory: false },
  },
  numberOfDoctoratesAwarded: {
    overall: { mandatory: true },
    subject: { mandatory: true },
  },
  numberOfResearchDoctoratesAwarded: {
    overall: { mandatory: true },
    subject: { mandatory: true },
  },
  numberOfProfessionalDoctoratesAwarded: {
    overall: { mandatory: true },
    subject: { mandatory: true },
  },
  totalInstitutionalIncome: {
    overall: { mandatory: true },
    subject: { mandatory: false },
  },
  researchIncome: {
    overall: { mandatory: true },
    subject: { mandatory: true },
  },
  researchIncomeFromIndustryAndCommerce: {
    overall: { mandatory: true },
    subject: { mandatory: false },
  },
  researchVotes: {
    overall: { mandatory: false },
    subject: { mandatory: false, ignored: true },
  },
  teachingVotes: {
    overall: { mandatory: false },
    subject: { mandatory: false, ignored: true },
  },
  totalPublications: {
    overall: { mandatory: false, ignored: true },
    subject: { mandatory: false },
  },
  citationScoreCnNormalised: {
    overall: { mandatory: false },
    subject: { mandatory: false, ignored: true },
  },
  citationScoreNormalised: {
    overall: { mandatory: false },
    subject: { mandatory: false, ignored: true },
  },
  internationalPublications: {
    overall: { mandatory: false, ignored: true },
    subject: { mandatory: false },
  },
};

export const wurReportersFieldsMeta: {
  [key in FieldKeys]: {
    overall?: {
      mandatory?: boolean;
      ignored?: boolean;
      maxValue?: number;
    };
    subject?: {
      mandatory?: boolean;
      ignored?: boolean;
      maxValue?: number;
    };
  };
} = {
  numberOfAcademicStaff: {
    overall: { mandatory: false, ignored: true },
    subject: { mandatory: false, ignored: true },
  },
  numberOfAcademicStaffOfInternationalOrOverseasOrigin: {
    overall: { mandatory: false, ignored: true },
    subject: { mandatory: false, ignored: true },
  },
  numberOfResearchStaff: {
    overall: { mandatory: false, ignored: true },
    subject: { mandatory: false, ignored: true },
  },
  totalNumberOfStudents: {
    overall: { mandatory: false, ignored: true },
    subject: { mandatory: false, ignored: true },
  },
  totalNumberOfStudentsOfInternationalOrOverseasOrigin: {
    overall: { mandatory: false, ignored: true },
    subject: { mandatory: false, ignored: true },
  },
  totalNumberOfOutboundExchangeStudents: {
    overall: { mandatory: false, ignored: true },
    subject: { mandatory: false, ignored: true },
  },
  percentileFieldWeightedCitationImpact: {
    overall: { mandatory: false, ignored: true },
    subject: { mandatory: false, ignored: true },
  },
  influenceOfPublications: {
    overall: { mandatory: false, ignored: true },
    subject: { mandatory: false, ignored: true },
  },
  numberOfPatentsCitingResearch: {
    overall: { mandatory: false, ignored: true },
    subject: { mandatory: false, ignored: true },
  },
  numberOfPublicationsInTheTop10Percentile: {
    overall: { mandatory: false, ignored: true },
    subject: { mandatory: false, ignored: true },
  },
  numberOfUndergraduateDegreesAwarded: {
    overall: { mandatory: false, ignored: true },
    subject: { mandatory: false, ignored: true },
  },
  numberOfDoctoratesAwarded: {
    overall: { mandatory: false, ignored: true },
    subject: { mandatory: false, ignored: true },
  },
  numberOfResearchDoctoratesAwarded: {
    overall: { mandatory: false, ignored: true },
    subject: { mandatory: false, ignored: true },
  },
  numberOfProfessionalDoctoratesAwarded: {
    overall: { mandatory: false, ignored: true },
    subject: { mandatory: false, ignored: true },
  },
  totalInstitutionalIncome: {
    overall: { mandatory: false, ignored: true },
    subject: { mandatory: false, ignored: true },
  },
  researchIncome: {
    overall: { mandatory: false, ignored: true },
    subject: { mandatory: false, ignored: true },
  },
  researchIncomeFromIndustryAndCommerce: {
    overall: { mandatory: false, ignored: true },
    subject: { mandatory: false, ignored: true },
  },
  researchVotes: {
    overall: { mandatory: false, ignored: true },
    subject: { mandatory: false, ignored: true },
  },
  teachingVotes: {
    overall: { mandatory: false, ignored: true },
    subject: { mandatory: false, ignored: true },
  },
  totalPublications: {
    overall: { mandatory: false, ignored: true },
    subject: { mandatory: false, ignored: true },
  },
  citationScoreCnNormalised: {
    overall: { mandatory: false, ignored: true, maxValue: 100 },
    subject: { mandatory: false, ignored: true, maxValue: 100 },
  },
  citationScoreNormalised: {
    overall: { mandatory: false, ignored: true, maxValue: 100 },
    subject: { mandatory: false, ignored: true, maxValue: 100 },
  },
  internationalPublications: {
    overall: { mandatory: false, ignored: true },
    subject: { mandatory: false, ignored: true },
  },
};

export const mandatoryOverallFields = [
  'numberOfAcademicStaff',
  'numberOfAcademicStaffOfInternationalOrOverseasOrigin',
  'numberOfResearchStaff',
  'totalNumberOfStudents',
  'totalNumberOfStudentsOfInternationalOrOverseasOrigin',
  'totalNumberOfOutboundExchangeStudents',
  'percentileFieldWeightedCitationImpact',
  'influenceOfPublications',
  'numberOfPatentsCitingResearch',
  'numberOfPublicationsInTheTop10Percentile',
  'numberOfUndergraduateDegreesAwarded',
  'numberOfDoctoratesAwarded',
  'numberOfResearchDoctoratesAwarded',
  'numberOfProfessionalDoctoratesAwarded',
  'totalInstitutionalIncome',
  'researchIncome',
  'researchIncomeFromIndustryAndCommerce',
];

export const mandatorySubjectFields = [
  'numberOfAcademicStaff',
  'numberOfResearchStaff',
  'numberOfDoctoratesAwarded',
  'numberOfResearchDoctoratesAwarded',
  'numberOfProfessionalDoctoratesAwarded',
  'researchIncome',
];

export type SubjectFields = {
  [key in FieldKeys]?: string | number;
} & {
  applicable?: boolean;
  missingFields?: number;
};

export type ErrorFields = {
  [key in FieldKeys]?: string;
} & {
  missingFields?: string;
};

export type FormErrors = {
  [key in SubjectId]?: ErrorFields;
} & {
  applicable?: string;
  name?: string;
  methodology?: string;
};

export type Values = { [key in SubjectId]: SubjectFields };
export type FormValues = {
  name: string;
  methodology: string;
} & Values;

type ScenarioParametersTableRow = {
  subject: string;
  measure: string;
  from: number | string;
  to: number | string;
};

export const scenarioValidator = (values: Values) => {
  const errors: FormErrors = {};

  let hasApplicableSubject: boolean = false;

  Object.entries(values)
    .filter(([, value]) => {
      if (typeof value === 'string') {
        return true;
      }
      return value.applicable;
    })
    .forEach(([key, value]) => {
      switch (key) {
        case 'name':
        case 'methodology':
          if (!value) {
            errors[key] = 'Invalid field';
          }
          break;

        default: {
          const subject = key as SubjectId;
          let errorMsg: string;
          let fieldKey: string;
          let subjectMissingFields: number = 0;

          // Initialise subject object
          errors[subject] = {};

          Object.entries(value)
            .filter(([innerKey]) => innerKey !== 'applicable')
            .forEach(([innerKey, innerValue]) => {
              const missingValue =
                innerValue === '' || innerValue === undefined;
              const fieldMeta =
                wurReportersFieldsMeta[innerKey as FieldKeys][
                  key === 'overall' ? key : 'subject'
                ];

              if (missingValue && fieldMeta?.mandatory) {
                subjectMissingFields += 1;
              }

              if (
                fieldMeta?.maxValue &&
                innerValue &&
                Number(innerValue) > fieldMeta?.maxValue
              ) {
                fieldKey = innerKey;
                errorMsg = `Number can not be greater than ${fieldMeta?.maxValue}`;
              }

              if (innerValue && Number(innerValue) < 0) {
                fieldKey = innerKey;
                errorMsg = `Number can not be less than 0`;
              }

              if (errorMsg) {
                // @ts-ignore
                errors[subject][fieldKey] = errorMsg;
              }
            });

          if (subjectMissingFields >= 3) {
            // @ts-ignore
            errors[subject].missingFields = 'Too many missing fields';
          }
          if (subject !== 'overall' && value.applicable) {
            hasApplicableSubject = true;
          }

          break;
        }
      }
    });

  if (!hasApplicableSubject) {
    errors.applicable = 'At least one subject should be selected with Overall';
  }

  // Filter out subjects without errors
  return Object.entries(errors).reduce((acc, [key, value]) => {
    if (typeof value === 'string' || !isEmpty(value)) {
      return { ...acc, [key]: value };
    }
    return acc;
  }, {});
};

export const initialValues = subjectIds.reduce(
  (acc, s) => ({
    ...acc,
    [s as SubjectId]: {
      applicable: s === 'overall',
      numberOfAcademicStaff: undefined,
      numberOfAcademicStaffOfInternationalOrOverseasOrigin: undefined,
      numberOfResearchStaff: undefined,
      totalNumberOfStudents: undefined,
      totalNumberOfStudentsOfInternationalOrOverseasOrigin: undefined,
      totalNumberOfOutboundExchangeStudents: undefined,
      percentileFieldWeightedCitationImpact: undefined,
      influenceOfPublications: undefined,
      numberOfPatentsCitingResearch: undefined,
      numberOfPublicationsInTheTop10Percentile: undefined,
      numberOfUndergraduateDegreesAwarded: undefined,
      numberOfDoctoratesAwarded: undefined,
      numberOfResearchDoctoratesAwarded: undefined,
      numberOfProfessionalDoctoratesAwarded: undefined,
      totalInstitutionalIncome: undefined,
      researchIncome: undefined,
      researchIncomeFromIndustryAndCommerce: undefined,
      researchVotes: undefined,
      teachingVotes: undefined,
      totalPublications: undefined,
      citationScoreCnNormalised: undefined,
      citationScoreNormalised: undefined,
      internationalPublications: undefined,
    },
  }),
  { name: 'New name', methodology: 'wur2003' } as FormValues,
);

export const initialErrors = {
  applicable: 'At least one subject should be selected with Overall',
  Overall: {
    missingFields: 'Too many missing fields',
  },
} as FormErrors;

export const subjectIdToLabel = (subjectId: SubjectId) =>
  subjectsMap[subjectId];

export const formatInputObject = (
  input: FormValues | ScenarioData,
  isWurReporterInterim?: boolean,
) =>
  Object.entries(input).reduce((acc, [key, value]) => {
    if (key === '__typename') {
      return acc;
    }

    if (typeof value === 'string') {
      return { ...acc, [key]: value };
    }

    const updatedValue = { ...value };

    if (isWurReporterInterim && typeof value === 'object') {
      Object.keys(value).forEach((currentKey) => {
        updatedValue[currentKey] =
          updatedValue[currentKey] === '' ? null : updatedValue[currentKey];
      });
    }

    const formattedValues = omit(['__typename'], updatedValue);

    return { ...acc, [key]: formattedValues };
  }, {} as FormValues);

export const getScenarioParametersTableData = (
  scenarioData: ScenarioDataType,
  submissionData: SubmissionDataType,
  scenarioMethodologyYear: number,
) => {
  const tableData: ScenarioParametersTableRow[] = [];

  const measuresMapForYear = getMeasuresMapForYear(scenarioMethodologyYear);
  const allowedFields =
    scenarioMethodologyYear > 2023
      ? getFormFieldsWur3ForYear(scenarioMethodologyYear)
      : getFormFieldsWur2ForYear(scenarioMethodologyYear);

  Object.keys(scenarioData)
    .filter(
      (key: string) =>
        subjectsMap[key as keyof typeof subjectsMap] &&
        scenarioData[key].applicable === true,
    )
    .forEach((subject: string) => {
      Object.keys(scenarioData[subject])
        .filter((key: string) => allowedFields.includes(key as FieldKeys))
        .forEach((measure) => {
          const label =
            measuresMapForYear[measure as keyof typeof measuresMapForYear] ??
            measure;

          tableData.push({
            subject: subjectIdToLabel(subject as SubjectId),
            measure: label,
            from:
              roundNumber(submissionData?.[subject]?.[measure], 2) ||
              'Unavailable',
            to: roundNumber(scenarioData[subject][measure], 2) || 'Unavailable',
          });
        });
    });
  return tableData;
};

export const getWUR20MetricsExplanation = [
  {
    metric: 'Doctorates awarded to bachelors degrees awarded',
    explanation:
      'This is about the relative intensity of advanced education. Institutions with a high density of research students compared to the rest of the student body are likely more knowledge-intensive.',
  },
  {
    metric: 'Doctorates awarded to academic staff',
    explanation:
      'As well as giving a sense of how committed an institution is to nurturing the next generation of academics, a high proportion of postgraduate research students also suggests the provision of teaching at the highest level that is thus attractive to graduates and effective at developing them.',
  },
  {
    metric: 'Teaching reputation',
    explanation:
      'This metric examines the perceived prestige of teaching at the university. Calculated from the votes received by the Academic Reputation Survey, weighted by subject and country to be representative of the distribution of academics globally.',
  },
  {
    metric: 'Institutional income to academic staff',
    explanation:
      'The total income per staff (PPP adjusted) is an indicator of the environment and status of an institution, giving a broad sense of the infrastructure and facilities available to students and staff.',
  },
  {
    metric: 'Students to academic staff ratio',
    explanation:
      'This is an indication of how many resources are dedicated to each student in terms of staffing.',
  },
  {
    metric: 'Publications per staff',
    explanation:
      'Publication output per head is a measure of the productivity of the institution, giving a sense of the institution’s ability to get papers published in quality peer-reviewed journals.',
  },
  {
    metric: 'Research income to academic staff',
    explanation:
      'The ability of a university to acquire funding for research is an indication of how competitive they are. This is a somewhat controversial indicator because it can be influenced by national policy and economic circumstances. Income is crucial to the development of world-class research, and because much of it is subject to competition and judged by peer review, our experts suggested that it was a valid measure.',
  },
  {
    metric: 'Research Reputation',
    explanation:
      'This metric examines the perceived prestige of research at the university. Calculated from the votes received by the Academic Reputation Survey, weighted by subject and country to be representative of the distribution of academics globally.',
  },
  {
    metric: 'Citations',
    explanation:
      'Our research influence indicator looks at universities’ role in spreading new knowledge and ideas. We examine research influence by capturing the average number of times a university’s published work is cited by scholars globally. The data is normalised by Elsevier to reflect variations in citation volume between different subject areas. This means that institutions with high levels of research activity in subjects with traditionally high citation counts do not gain an unfair advantage',
  },
  {
    metric: 'Industry income to academic staff',
    explanation:
      'This metric seeks to capture knowledge-transfer activity. An institution’s ability to help industry with innovations, inventions and consultancy has become a core mission of the contemporary global academy. This category suggests the extent to which businesses are willing to pay for research and an institution’s ability to attract funding in the commercial marketplace – useful indicators of institutional quality.',
  },
  {
    metric: 'Proportion of international academic staff',
    explanation:
      'This metric captures the proportion of international academic staff on campus. International staff are those whose nationality differs from the country where the institution is based.',
  },
  {
    metric: 'International co-authorship',
    explanation:
      'In the third international indicator, we calculate the proportion of an institution’s total research journal publications that have at least one international co-author. The ability to attract international co-authors as an indicator of quality.',
  },
  {
    metric: 'Proportion of international students',
    explanation:
      'This metric captures the proportion of international students on campus. International students are those whose nationality differs from the country where the institution is based.',
  },
];

export const getWUR30MetricsExplanation = [
  {
    metric: 'Teaching Reputation',
    explanation:
      'It examined the perceived prestige of institutions in teaching. This metric is the total number of votes obtained from the reputation survey from the last two years. Each year is calculated as the number of global teaching votes from respondents of the reputation survey, weighted by subject and country to be representative of the distribution of academics globally. Only non-zero values will be standardised using a logarithmic function, and universities that received no votes are scored a zero for this metric.',
  },
  {
    metric: 'Student Staff Ratio',
    explanation:
      'The student staff ratio is defined as total full time equivalent (FTE) number of staff employed in an academic post divided by FTE number of students in all years and of all programmes that lead to a degree, certificate, university credit or other qualification. This variable is normalised after calculation.',
  },
  {
    metric: 'Doctorate Bachelor Ratio',
    explanation:
      'This metric is generated by dividing the total number of doctorates awarded by the total number of undergraduate degrees awarded. This variable is normalised after calculation.',
  },
  {
    metric: 'Doctorate Staff Ratio',
    explanation:
      'As well as giving a sense of how committed an institution is to nurturing the next generation of academics, a high proportion of postgraduate research students also suggests the provision of teaching at the highest level that is thus attractive to graduates and effective at developing them. This metric is generated by dividing the total subject weighted doctorates, by the total subject weighted number of academic staff. This metric takes into account an institution’s unique subject mix, reflecting that the volume of doctoral awards varies by discipline. This variable is normalised after calculation.',
  },
  {
    metric: 'Institutional Income',
    explanation:
      'This measure of income indicates an institution’s general status and gives a broad sense of the infrastructure and facilities available to students and staff. This metric is generated by dividing the institutional income adjusted to PPP, by the total number of academic staff. This variable is normalised after calculation.',
  },
  {
    metric: 'Research Reputation',
    explanation:
      'It examined the perceived prestige of institutions in research. This metric is the total number of votes obtained from the reputation survey from the last two years. Each year is calculated as the number of global research votes from respondents of the reputation survey, weighted by subject and country to be representative of the distribution of academics globally. Only non-zero values will be standardised using a logarithmic function, and universities that received no votes are scored a zero for this metric.',
  },
  {
    metric: 'Research Income',
    explanation:
      'This metric is generated by dividing the total subject weighted research income adjusted for PPP, by the total subject weighted number of academic staff and is normalised after calculation. This is a somewhat controversial indicator because it can be influenced by national policy and economic circumstances. Income is crucial to the development of world-class research, and because much of it is subject to competition and judged by peer review, our experts suggested that it was a valid measure. This indicator takes account of each institution’s distinct subject profile, reflecting the fact that research grants in science subjects are often bigger than those awarded for the highest-quality social science, arts and humanities research.',
  },
  {
    metric: 'Research Productivity',
    explanation:
      'This metric is generated by dividing the total subject weighted number of papers published in the academic journals indexed by Elsevier’s Scopus database per scholar, divided by the sum of the total subject weighted number of FTE research staff and FTE academic staff. This metric is normalised after calculation. The indicator gives a sense of the institution’s ability to get papers published in quality peer-reviewed journals.',
  },
  {
    metric: 'Citation Impact',
    explanation:
      'Our research influence metric looks at universities’ role in spreading new knowledge and ideas. We examine research influence by capturing the average number of times a university’s published work is cited by scholars globally. We look at the academic journals indexed by Elsevier’s Scopus database and all indexed publications from a five-year window. Citations to these publications are also collected. The data is normalised by Elsevier to reflect variations in citation volume between different subject areas. This means that institutions with high levels of research activity in subjects with traditionally high citation counts do not gain an unfair advantage. We have blended equal measures of a country-adjusted and non-country-adjusted raw measure of citations scores.',
  },
  {
    metric: 'Research Strength',
    explanation:
      'Our research strength metric measures the representative research quality based on the FWCI, but without being skewed by papers with an exceptionally high FWCI. It captures the 75th percentile of the Field-Weighted Citation Impact (FWCI) of all papers published by a university. We look at all Scopus-indexed publications from a five-year window.',
  },
  {
    metric: 'Research Excellence',
    explanation:
      'This metric recognises the institution’s contribution to the best research in each subject and overall​. Excellence is measured by capturing the total number of publications by an institution that are among the top 10% of publications worldwide by FWCI. We adjust this number by year, subject, and the total number of academic and research staff.',
  },
  {
    metric: 'Research Influence',
    explanation:
      'This metric analyses the influence of an entity’s publications by analysing their corresponding citations. The importance of a publication is determined based on the importance of other papers citing it. We adjust this number by year, subject, and the total number of academic and research staff.',
  },
  {
    metric: 'Industry Income',
    explanation:
      'An institution’s ability to help industry with innovations, inventions and consultancy has become a core mission of the contemporary global academy. This category suggests the extent to which businesses are willing to pay for research and an institution’s ability to attract funding in the commercial marketplace – useful indicators of institutional quality. The indicator seeks to capture such knowledge-transfer activity by looking at how much research income an institution earns from industry (adjusted for PPP), divided by the total number of FTE academic staff it employs. This variable is normalised after calculation.',
  },
  {
    metric: 'Patents',
    explanation:
      'This metric recognises the extent to which universities are supporting their national economies through technology transfer. It measures the count of patents citing an entity’s published research. This measure is subject weighted to avoid penalising universities producing research in fields low in patents. We also normalise this by the sum of academic and research staff.',
  },
  {
    metric: 'International Students',
    explanation:
      'This metric captures the proportion of international students on campus. International students are those whose nationality differs from the country where the institution is based. The metric is calculated as the total FTE number of international students divided by the total FTE number of students. This variable is normalised to account for the country population’s size.',
  },
  {
    metric: 'International Staff',
    explanation:
      'This metric captures the proportion of international academic staff on campus. International staff are those whose nationality differs from the country where the institution is based. The metric is calculated as the total FTE number of international academic staff divided by the total FTE number of academic staff. This variable is normalised to account for the country population’s size.',
  },
  {
    metric: 'International Co-authorship',
    explanation:
      'In the third international indicator, we calculate the proportion of an institution’s total research journal publications that have at least one international co-author. The metric is generated by dividing the total subject weighted number of publications with at least one international co-author by the total subjected weighted number of publications. This accounts for an institution’s subject mix. This variable is normalised to account for the country population’s size.',
  },
  {
    metric: 'Studying Abroad',
    explanation:
      'Based on the number of outbound exchange students, this metric assesses international student mobility. The headcount number of exchange students going abroad is divided by the total FTE number of students. This metric is subject-weighted and adjusted by the country’s population. It has a zero weight for the 2024 ranking calculation.',
  },
];

export const getWUR31MetricsExplanation = [
  {
    metric: 'Teaching Reputation',
    explanation:
      'It examined the perceived prestige of institutions in teaching. This metric is the total number of votes obtained from the reputation survey from the last two years. Each year is calculated as the number of global teaching votes from respondents of the reputation survey, weighted by subject and country to be representative of the distribution of academics globally. Only non-zero values will be standardised using a logarithmic function, and universities that received no votes are scored a zero for this metric.',
  },
  {
    metric: 'Student Staff Ratio',
    explanation:
      'The student staff ratio is defined as total full time equivalent (FTE) number of staff employed in an academic post divided by FTE number of students in all years and of all programmes that lead to a degree, certificate, university credit or other qualification. This variable is normalised after calculation.',
  },
  {
    metric: 'Doctorate Bachelor Ratio',
    explanation:
      'This metric is generated by dividing the total number of doctorates awarded by the total number of undergraduate degrees awarded. This variable is normalised after calculation.',
  },
  {
    metric: 'Doctorate Staff Ratio',
    explanation:
      'As well as giving a sense of how committed an institution is to nurturing the next generation of academics, a high proportion of postgraduate research students also suggests the provision of teaching at the highest level that is thus attractive to graduates and effective at developing them. This metric is generated by dividing the total subject weighted doctorates, by the total subject weighted number of academic staff. This metric takes into account an institution’s unique subject mix, reflecting that the volume of doctoral awards varies by discipline. This variable is normalised after calculation.',
  },
  {
    metric: 'Institutional Income',
    explanation:
      'This measure of income indicates an institution’s general status and gives a broad sense of the infrastructure and facilities available to students and staff. This metric is generated by dividing the institutional income adjusted to PPP, by the total number of academic staff. This variable is normalised after calculation.',
  },
  {
    metric: 'Research Reputation',
    explanation:
      'It examined the perceived prestige of institutions in research. This metric is the total number of votes obtained from the reputation survey from the last two years. Each year is calculated as the number of global research votes from respondents of the reputation survey, weighted by subject and country to be representative of the distribution of academics globally. Only non-zero values will be standardised using a logarithmic function, and universities that received no votes are scored a zero for this metric.',
  },
  {
    metric: 'Research Income',
    explanation:
      'This metric is generated by dividing the total subject weighted research income adjusted for PPP, by the total subject weighted number of academic staff and is normalised after calculation. This is a somewhat controversial indicator because it can be influenced by national policy and economic circumstances. Income is crucial to the development of world-class research, and because much of it is subject to competition and judged by peer review, our experts suggested that it was a valid measure. This indicator takes account of each institution’s distinct subject profile, reflecting the fact that research grants in science subjects are often bigger than those awarded for the highest-quality social science, arts and humanities research.',
  },
  {
    metric: 'Research Productivity',
    explanation:
      'This metric is generated by dividing the total subject weighted number of papers published in the academic journals indexed by Elsevier’s Scopus database per scholar, divided by the sum of the total subject weighted number of FTE research staff and FTE academic staff. This metric is normalised after calculation. The indicator gives a sense of the institution’s ability to get papers published in quality peer-reviewed journals.',
  },
  {
    metric: 'Citation Impact',
    explanation:
      'Our research influence metric looks at universities’ role in spreading new knowledge and ideas. We examine research influence by capturing the average number of times a university’s published work is cited by scholars globally. We look at the academic journals indexed by Elsevier’s Scopus database and all indexed publications from a five-year window. Citations to these publications are also collected. The data is normalised by Elsevier to reflect variations in citation volume between different subject areas. This means that institutions with high levels of research activity in subjects with traditionally high citation counts do not gain an unfair advantage. We have blended equal measures of a country-adjusted and non-country-adjusted raw measure of citations scores.',
  },
  {
    metric: 'Research Strength',
    explanation:
      'Our research strength metric measures the representative research quality based on the FWCI, but without being skewed by papers with an exceptionally high FWCI. It captures the 75th percentile of the Field-Weighted Citation Impact (FWCI) of all papers published by a university. We look at all Scopus-indexed publications from a five-year window.',
  },
  {
    metric: 'Research Excellence',
    explanation:
      'This metric recognises the institution’s contribution to the best research in each subject and overall​. Excellence is measured by capturing the total number of publications by an institution that are among the top 10% of publications worldwide by FWCI. We adjust this number by year, subject, and the total number of academic and research staff.',
  },
  {
    metric: 'Research Influence',
    explanation:
      'This metric analyses the influence of an entity’s publications by analysing their corresponding citations. The importance of a publication is determined based on the importance of other papers citing it. We adjust this number by year, subject, and the total number of academic and research staff.',
  },
  {
    metric: 'Industry Income',
    explanation:
      'An institution’s ability to help industry with innovations, inventions and consultancy has become a core mission of the contemporary global academy. This category suggests the extent to which businesses are willing to pay for research and an institution’s ability to attract funding in the commercial marketplace – useful indicators of institutional quality. The indicator seeks to capture such knowledge-transfer activity by looking at how much research income an institution earns from industry (adjusted for PPP), divided by the total number of FTE academic staff it employs. This variable is normalised after calculation.',
  },
  {
    metric: 'Patents',
    explanation:
      'This metric recognises the extent to which universities are supporting their national economies through technology transfer. It measures the count of patents citing an entity’s published research. This measure is subject weighted to avoid penalising universities producing research in fields low in patents. We also normalise this by the sum of academic and research staff.',
  },
  {
    metric: 'International Students',
    explanation:
      'This metric captures the proportion of international students on campus. International students are those whose nationality differs from the country where the institution is based. The metric is calculated as the total FTE number of international students divided by the total FTE number of students. This variable is normalised to account for the country population’s size.',
  },
  {
    metric: 'International Staff',
    explanation:
      'This metric captures the proportion of international academic staff on campus. International staff are those whose nationality differs from the country where the institution is based. The metric is calculated as the total FTE number of international academic staff divided by the total FTE number of academic staff. This variable is normalised to account for the country population’s size.',
  },
  {
    metric: 'International Co-authorship',
    explanation:
      'In the third international indicator, we calculate the proportion of an institution’s total research journal publications that have at least one international co-author. The metric is generated by dividing the total subject weighted number of publications with at least one international co-author by the total subjected weighted number of publications. This accounts for an institution’s subject mix. This variable is normalised to account for the country population’s size.',
  },
];

export const wur20Columns = [
  {
    Header: 'Doctorates to academic staff',
    accessor: 'doctoratesToAcademicStaff',
  },
  {
    Header: 'Doctorates to bachelor’s degrees awarded',
    accessor: 'doctoratesToBachelorsDegreesAwarded',
  },
  {
    Header: 'Teaching Reputation',
    accessor: 'teachingReputation',
  },
  {
    Header: 'Income to academic staff',
    accessor: 'incomeToAcademicStaff',
  },
  {
    Header: 'Students to academic staff ratio',
    accessor: 'studentsToAcademicStaffRatio',
  },
  {
    Header: 'Publications per academic staff',
    accessor: 'publicationsPerAcademicStaff',
  },
  {
    Header: 'Research income to academic staff',
    accessor: 'researchIncomeToAcademicStaff',
  },
  {
    Header: 'Research reputation',
    accessor: 'researchReputation',
  },
  {
    Header: 'Citations',
    accessor: 'citations',
  },
  {
    Header: 'Industry income to academic staff',
    accessor: 'industryIncomeToAcademicStaff',
  },
  {
    Header: 'Proportion of international academic staff',
    accessor: 'proportionOfInternationalAcademicStaff',
  },
  {
    Header: 'International co-authorship',
    accessor: 'internationalCoAuthorship',
  },
  {
    Header: 'Proportion of international students',
    accessor: 'proportionOfInternationalStudents',
  },
];

export const wur30Columns = [
  {
    Header: 'Teaching reputation',
    accessor: 'teachingReputation',
  },
  {
    Header: 'Student staff ratio',
    accessor: 'studentStaffRatio',
  },
  {
    Header: 'Doctorate bachelor ratio',
    accessor: 'doctoratBbachelorRatio',
  },
  {
    Header: 'Doctorate staff ratio',
    accessor: 'doctorateStaffRatio',
  },
  {
    Header: 'Institutional income',
    accessor: 'institutionalIncome',
  },
  {
    Header: 'Research reputation',
    accessor: 'researchReputation',
  },
  {
    Header: 'Research income',
    accessor: 'researchIncome',
  },
  {
    Header: 'Research productivity',
    accessor: 'researchProductivity',
  },
  {
    Header: 'Citation Impact',
    accessor: 'citationImpact',
  },
  {
    Header: 'Research strength',
    accessor: 'researchStrength',
  },
  {
    Header: 'Research excellence',
    accessor: 'researchExcellence',
  },
  {
    Header: 'Research influence',
    accessor: 'researchInfluence',
  },
  {
    Header: 'Industry income',
    accessor: 'industryIncome',
  },
  {
    Header: 'Patents',
    accessor: 'patents',
  },
  {
    Header: 'International students',
    accessor: 'internationalStudents',
  },
  {
    Header: 'International staff',
    accessor: 'internationalStaff',
  },
  {
    Header: 'International co-authorship',
    accessor: 'internationalCoAuthorship',
  },
  {
    Header: 'Studying abroad',
    accessor: 'studyingAbroad',
  },
];

export const wur31Columns = [
  {
    Header: 'Teaching reputation',
    accessor: 'teachingReputation',
  },
  {
    Header: 'Student staff ratio',
    accessor: 'studentStaffRatio',
  },
  {
    Header: 'Doctorate bachelor ratio',
    accessor: 'doctoratBbachelorRatio',
  },
  {
    Header: 'Doctorate staff ratio',
    accessor: 'doctorateStaffRatio',
  },
  {
    Header: 'Institutional income',
    accessor: 'institutionalIncome',
  },
  {
    Header: 'Research reputation',
    accessor: 'researchReputation',
  },
  {
    Header: 'Research income',
    accessor: 'researchIncome',
  },
  {
    Header: 'Research productivity',
    accessor: 'researchProductivity',
  },
  {
    Header: 'Citation Impact',
    accessor: 'citationImpact',
  },
  {
    Header: 'Research strength',
    accessor: 'researchStrength',
  },
  {
    Header: 'Research excellence',
    accessor: 'researchExcellence',
  },
  {
    Header: 'Research influence',
    accessor: 'researchInfluence',
  },
  {
    Header: 'Industry income',
    accessor: 'industryIncome',
  },
  {
    Header: 'Patents',
    accessor: 'patents',
  },
  {
    Header: 'International students',
    accessor: 'internationalStudents',
  },
  {
    Header: 'International staff',
    accessor: 'internationalStaff',
  },
  {
    Header: 'International co-authorship',
    accessor: 'internationalCoAuthorship',
  },
];

export const getWUR20MetricsTable = [
  {
    empty: '% weighting',
    doctoratesToAcademicStaff: 6,
    doctoratesToBachelorsDegreesAwarded: 2.25,
    teachingReputation: 15,
    incomeToAcademicStaff: 2.25,
    studentsToAcademicStaffRatio: 4.5,
    publicationsPerAcademicStaff: 6,
    researchIncomeToAcademicStaff: 6,
    researchReputation: 18,
    citations: 30,
    industryIncomeToAcademicStaff: 2.5,
    proportionOfInternationalAcademicStaff: 2.5,
    internationalCoAuthorship: 2.5,
    proportionOfInternationalStudents: 2.5,
  },
  {
    empty: 'Scoring',
    doctoratesToAcademicStaff: 'Z',
    doctoratesToBachelorsDegreesAwarded: 'Z',
    teachingReputation: 'Exp.',
    incomeToAcademicStaff: 'Z',
    studentsToAcademicStaffRatio: 'Z',
    publicationsPerAcademicStaff: 'Z',
    researchIncomeToAcademicStaff: 'Z',
    researchReputation: 'Exp.',
    citations: 'Z',
    industryIncomeToAcademicStaff: 'Z',
    proportionOfInternationalAcademicStaff: 'Z',
    internationalCoAuthorship: 'Z',
    proportionOfInternationalStudents: 'Z',
  },
  {
    empty: 'ppp',
    doctoratesToAcademicStaff: '-',
    doctoratesToBachelorsDegreesAwarded: '-',
    teachingReputation: '-',
    incomeToAcademicStaff: 'Yes',
    studentsToAcademicStaffRatio: '-',
    publicationsPerAcademicStaff: '-',
    researchIncomeToAcademicStaff: 'Yes',
    researchReputation: '-',
    citations: '-',
    industryIncomeToAcademicStaff: 'Yes',
    proportionOfInternationalAcademicStaff: '-',
    internationalCoAuthorship: '-',
    proportionOfInternationalStudents: '-',
  },
  {
    empty: 'Subject weight',
    doctoratesToAcademicStaff: 'Yes',
    doctoratesToBachelorsDegreesAwarded: '-',
    teachingReputation: '-',
    incomeToAcademicStaff: '-',
    studentsToAcademicStaffRatio: '-',
    publicationsPerAcademicStaff: 'Yes',
    researchIncomeToAcademicStaff: 'Yes',
    researchReputation: '-',
    citations: '-',
    industryIncomeToAcademicStaff: '-',
    proportionOfInternationalAcademicStaff: '-',
    internationalCoAuthorship: '-',
    proportionOfInternationalStudents: 'Yes',
  },
  {
    empty: 'Input section',
    doctoratesToAcademicStaff: 'Subject',
    doctoratesToBachelorsDegreesAwarded: 'Overall',
    teachingReputation: 'Overall',
    incomeToAcademicStaff: 'Overall',
    studentsToAcademicStaffRatio: 'Overall',
    publicationsPerAcademicStaff: 'Subject',
    researchIncomeToAcademicStaff: 'Subject',
    researchReputation: 'Overall',
    citations: 'Overall',
    industryIncomeToAcademicStaff: 'Overall',
    proportionOfInternationalAcademicStaff: 'Overall',
    internationalCoAuthorship: 'Overall',
    proportionOfInternationalStudents: 'Subject',
  },
  {
    empty: 'Input data',
    doctoratesToAcademicStaff: 'Degrees: Doctorates awarded',
    doctoratesToBachelorsDegreesAwarded: 'Degrees: Doctorates awarded',
    teachingReputation:
      'Teaching reputation votes (THE Global Academic Survey)',
    incomeToAcademicStaff: 'Income: Total',
    studentsToAcademicStaffRatio: 'Staff: Academic',
    publicationsPerAcademicStaff: 'Publications: Total (Elsevier’s Scopus)',
    researchIncomeToAcademicStaff: 'Income: Research',
    researchReputation:
      'Research reputation votes (THE Global Academic Survey)',
    citations: 'Field weighted citation impact (Elsevier’s Scopus)',
    industryIncomeToAcademicStaff: 'Income: Research industry and commerce',
    proportionOfInternationalAcademicStaff: 'Staff: International academic',
    internationalCoAuthorship: 'Student: International',
    proportionOfInternationalStudents:
      'Publications: Internationally co-authored (Elsevier’s Scopus)',
  },
  {
    empty: 'Input data',
    doctoratesToAcademicStaff: 'Staff: Academic',
    doctoratesToBachelorsDegreesAwarded: 'Degrees: Undergraduate awarded',
    teachingReputation: '-',
    incomeToAcademicStaff: 'Student: Total',
    studentsToAcademicStaffRatio: 'Staff: Academic',
    publicationsPerAcademicStaff: 'Staff Academic',
    researchIncomeToAcademicStaff: 'Staff Academic',
    researchReputation: '-',
    citations: '-',
    industryIncomeToAcademicStaff: 'Staff Academic',
    proportionOfInternationalAcademicStaff: 'Staff Academic',
    internationalCoAuthorship: 'Student: Total',
    proportionOfInternationalStudents:
      'Publications: Total (Elsevier’s Scopus)',
  },
];

export const getWUR30MetricsTable = [
  {
    empty: 'Pillar',
    teachingReputation: 'Teaching',
    studentStaffRatio: 'Teaching',
    doctoratBbachelorRatio: 'Teaching',
    doctorateStaffRatio: 'Teaching',
    institutionalIncome: 'Teaching',
    researchReputation: 'Research Environment',
    researchIncome: 'Research Environment',
    researchProductivity: 'Research Environment',
    citationImpact: 'Research Quality',
    researchStrength: 'Research Quality',
    researchExcellence: 'Research Quality',
    researchInfluence: 'Research Quality',
    industryIncome: 'Industry',
    patents: 'Industry',
    internationalStudents: 'International',
    internationalStaff: 'International',
    internationalCoAuthorship: 'International',
    studyingAbroad: 'International',
  },
  {
    empty: 'Weight',
    teachingReputation: '15%',
    studentStaffRatio: '4.5%',
    doctoratBbachelorRatio: '2%',
    doctorateStaffRatio: '5.5%',
    institutionalIncome: '2.5%',
    researchReputation: '18%',
    researchIncome: '5.5%',
    researchProductivity: '5.5%',
    citationImpact: '15%',
    researchStrength: '5%',
    researchExcellence: '5%',
    researchInfluence: '5%',
    industryIncome: '2%',
    patents: '2%',
    internationalStudents: '2.5%',
    internationalStaff: '2.5%',
    internationalCoAuthorship: '2.5%',
    studyingAbroad: '0%',
  },
  {
    empty: 'Scoring',
    teachingReputation: 'Exp',
    studentStaffRatio: 'Normal',
    doctoratBbachelorRatio: 'Normal',
    doctorateStaffRatio: 'Normal',
    institutionalIncome: 'Normal',
    researchReputation: 'Exp',
    researchIncome: 'Normal',
    researchProductivity: 'Normal',
    citationImpact: '',
    researchStrength: 'Normal',
    researchExcellence: 'Exp',
    researchInfluence: 'Exp',
    industryIncome: 'Normal',
    patents: 'Exp',
    internationalStudents: 'Normal',
    internationalStaff: 'Normal',
    internationalCoAuthorship: 'Normal',
    studyingAbroad: 'Normal',
  },
  {
    empty: 'PPP',
    teachingReputation: '',
    studentStaffRatio: '',
    doctoratBbachelorRatio: '',
    doctorateStaffRatio: '',
    institutionalIncome: 'Yes',
    researchReputation: '',
    researchIncome: 'Yes',
    researchProductivity: '',
    citationImpact: '',
    researchStrength: '',
    researchExcellence: '',
    researchInfluence: '',
    industryIncome: 'Yes',
    patents: '',
    internationalStudents: '',
    internationalStaff: '',
    internationalCoAuthorship: '',
    studyingAbroad: '',
  },
  {
    empty: 'Input data',
    teachingReputation: 'Teaching votes',
    studentStaffRatio: `Total academic staff
    students total`,
    doctoratBbachelorRatio: `Doctorates awarded
    Undergraduate degrees awarded`,
    doctorateStaffRatio: `Doctorates awarded
    Total academic staff`,
    institutionalIncome: `Total institutional income
    Total academic staff`,
    researchReputation: 'Research votes',
    researchIncome: `Total research income
    Total academic staff`,
    researchProductivity: `Total publications
    Total academic staff
    Total research staff`,
    citationImpact: `Citation score
    Country normalised citation score`,
    researchStrength: '75th Percentile field weighted citation impact',
    researchExcellence: `Top papers
    Total academic staff
    Total research staff`,
    researchInfluence: `Pagerank
    Total academic staff
    Total research staff`,
    industryIncome: `Research income from industry and commerce
    Total academic staff`,
    patents: `Patents
    Total academic staff
    Total research staff`,
    internationalStudents: `International students
    Total students`,
    internationalStaff: `International academic staff
    Total academic staff`,
    internationalCoAuthorship: `International publications
    Total publications`,
    studyingAbroad: `Students on outbound exchange
    Total students`,
  },
  {
    empty: 'Input Section',
    teachingReputation: 'Overall',
    studentStaffRatio: 'Overall',
    doctoratBbachelorRatio: 'Overall',
    doctorateStaffRatio: 'Subject',
    institutionalIncome: 'Overall',
    researchReputation: 'Overall',
    researchIncome: 'Subject',
    researchProductivity: 'Subject',
    citationImpact: 'Overall',
    researchStrength: 'Overall',
    researchExcellence: 'Subject',
    researchInfluence: 'Subject',
    industryIncome: 'Overall',
    patents: 'Subject',
    internationalStudents: 'Overall',
    internationalStaff: 'Overall',
    internationalCoAuthorship: 'Subject',
    studyingAbroad: 'Subject',
  },
];

export const getWUR31MetricsTable = [
  {
    empty: 'Pillar',
    teachingReputation: 'Teaching',
    studentStaffRatio: 'Teaching',
    doctoratBbachelorRatio: 'Teaching',
    doctorateStaffRatio: 'Teaching',
    institutionalIncome: 'Teaching',
    researchReputation: 'Research Environment',
    researchIncome: 'Research Environment',
    researchProductivity: 'Research Environment',
    citationImpact: 'Research Quality',
    researchStrength: 'Research Quality',
    researchExcellence: 'Research Quality',
    researchInfluence: 'Research Quality',
    industryIncome: 'Industry',
    patents: 'Industry',
    internationalStudents: 'International',
    internationalStaff: 'International',
    internationalCoAuthorship: 'International',
  },
  {
    empty: 'Weight',
    teachingReputation: '15%',
    studentStaffRatio: '4.5%',
    doctoratBbachelorRatio: '2%',
    doctorateStaffRatio: '5.5%',
    institutionalIncome: '2.5%',
    researchReputation: '18%',
    researchIncome: '5.5%',
    researchProductivity: '5.5%',
    citationImpact: '15%',
    researchStrength: '5%',
    researchExcellence: '5%',
    researchInfluence: '5%',
    industryIncome: '2%',
    patents: '2%',
    internationalStudents: '2.5%',
    internationalStaff: '2.5%',
    internationalCoAuthorship: '2.5%',
  },
  {
    empty: 'Scoring',
    teachingReputation: 'Exp',
    studentStaffRatio: 'Normal',
    doctoratBbachelorRatio: 'Normal',
    doctorateStaffRatio: 'Normal',
    institutionalIncome: 'Normal',
    researchReputation: 'Exp',
    researchIncome: 'Normal',
    researchProductivity: 'Normal',
    citationImpact: '',
    researchStrength: 'Normal',
    researchExcellence: 'Exp',
    researchInfluence: 'Exp',
    industryIncome: 'Normal',
    patents: 'Exp',
    internationalStudents: 'Normal',
    internationalStaff: 'Normal',
    internationalCoAuthorship: 'Normal',
  },
  {
    empty: 'PPP',
    teachingReputation: '',
    studentStaffRatio: '',
    doctoratBbachelorRatio: '',
    doctorateStaffRatio: '',
    institutionalIncome: 'Yes',
    researchReputation: '',
    researchIncome: 'Yes',
    researchProductivity: '',
    citationImpact: '',
    researchStrength: '',
    researchExcellence: '',
    researchInfluence: '',
    industryIncome: 'Yes',
    patents: '',
    internationalStudents: '',
    internationalStaff: '',
    internationalCoAuthorship: '',
  },
  {
    empty: 'Input data',
    teachingReputation: 'Teaching votes',
    studentStaffRatio: `Total academic staff
    students total`,
    doctoratBbachelorRatio: `Doctorates awarded
    Undergraduate degrees awarded`,
    doctorateStaffRatio: `Doctorates awarded
    Total academic staff`,
    institutionalIncome: `Total institutional income
    Total academic staff`,
    researchReputation: 'Research votes',
    researchIncome: `Total research income
    Total academic staff`,
    researchProductivity: `Total publications
    Total academic staff
    Total research staff`,
    citationImpact: `Citation score
    Country normalised citation score`,
    researchStrength: '75th Percentile field weighted citation impact',
    researchExcellence: `Top papers
    Total academic staff
    Total research staff`,
    researchInfluence: `Pagerank
    Total academic staff
    Total research staff`,
    industryIncome: `Research income from industry and commerce
    Total academic staff`,
    patents: `Patents
    Total academic staff
    Total research staff`,
    internationalStudents: `International students
    Total students`,
    internationalStaff: `International academic staff
    Total academic staff`,
    internationalCoAuthorship: `International publications
    Total publications`,
  },
  {
    empty: 'Input Section',
    teachingReputation: 'Overall',
    studentStaffRatio: 'Overall',
    doctoratBbachelorRatio: 'Overall',
    doctorateStaffRatio: 'Subject',
    institutionalIncome: 'Overall',
    researchReputation: 'Overall',
    researchIncome: 'Subject',
    researchProductivity: 'Subject',
    citationImpact: 'Overall',
    researchStrength: 'Overall',
    researchExcellence: 'Subject',
    researchInfluence: 'Subject',
    industryIncome: 'Overall',
    patents: 'Subject',
    internationalStudents: 'Overall',
    internationalStaff: 'Overall',
    internationalCoAuthorship: 'Subject',
  },
];

export type Scenario =
  | {
      scenarioId: string;
      name: string;
      methodology: string;
      createdAt: string;
      updatedAt: string;
      alexanderJson: any;
    }[]
  | undefined;

export const aggregateLabels = [
  { label: 'Top*', aggregateKey: 'max' },
  { label: '75%', aggregateKey: 'upperQuartile' },
  { label: 'Median', aggregateKey: 'median' },
  { label: '25%', aggregateKey: 'lowerQuartile' },
  { label: 'Bottom*', aggregateKey: 'min' },
];

export const getIsWurReporterInterimPage = () =>
  window.location.href.includes('wur-reporter-interim');

export const getIsRankedUniversity = (data: any[]) =>
  data.reduce((prev, curr) => prev || curr.result !== null, false);

export const defineMethodology = (methodologyYear: number = 2023) =>
  `WUR ${methodologyYear === 2023 ? 2 : 3}.0 - ${methodologyYear}`;

export const getNormalizeDataSubmission = (data: SubmissionWur2Query) => {
  const { submission } = data;

  if (!submission) {
    return initialValues;
  }
  const omitSubmission = omit(
    ['institutionId', '__typename', 'year', 'id'],
    submission,
  );

  const submissionWithoutTypenameWithUndefinedFields = Object.entries(
    omitSubmission,
  ).reduce((acc, [key, value]) => {
    const omitValue: Omit<SubmissionsSubjectField, '__typename'> = omit(
      ['__typename'],
      value,
    );
    Object.entries(omitValue).forEach(([position, count]) => {
      const result =
        omitValue[position as keyof typeof omitValue] === null
          ? (omitValue[position as keyof typeof omitValue] = undefined)
          : // @ts-ignore
            (omitValue[position] = count);
      return result;
    });
    return { ...acc, [key]: omit(['__typename'], omitValue) };
  }, {});

  return {
    ...initialValues,
    ...submissionWithoutTypenameWithUndefinedFields,
  };
};

export const getScenarioMethodologyYear = (
  methodology: string = 'WUR 2.0 - 2023',
) => Number(methodology.slice(-4));
